import CalendarEvent, { CalendarItemTypeTypeEx } from 'owa-calendar-types/lib/types/CalendarEvent';
import EventScope from 'owa-service/lib/contract/EventScope';
import getItemIdToUpdate from '../utils/getItemIdToUpdate';
import { isBefore } from 'owa-datetime';
import strings from 'owa-strings/owa-calendar-strings';
import { addOrModifyItemInCache } from '../utils/SharedCacheFunctions';
import { ControlIcons } from 'owa-control-icons';
import { DateRange } from 'owa-datetime-utils';
import { expandRecurringItem } from '../utils/expandRecurringItem';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isEventsCacheInitialized } from '../selectors/eventsCacheSelectors';
import { isException, isOccurrence, isRecurringMaster } from 'owa-calendar-event-capabilities';
import { lazyShowCalendarNotification } from 'owa-calendar-notification-bar';
import { orchestrator } from 'satcheljs';
import { prepareSeriesItemsForInstantUpdate } from '../utils/InstantUpdatePreparationFunctions';
import {
    getCalendarEventWithId,
    filterCalendarEvents,
} from '../selectors/calendarFolderEventsSelectors';
import {
    addCalendarEventsWithinCurrentLockedDateRanges,
    removeCalendarEventsFromEventsCacheMatchingFilter,
    updateItem,
} from '../actions/eventsCacheActions';
import {
    instantCalendarEventUpdate,
    CalendarEventOperationResult,
    calendarEventUpdated,
    updatingCalendarEventFailed,
} from '../actions/publicActions';

orchestrator(instantCalendarEventUpdate, async actionMessage => {
    const {
        eventId,
        eventChanges,
        errorMessage,
        actionToExecute,
        onEventUpdateFailed,
        onEventUpdateSuccess,
        rangeToCreate,
        showNotification,
    } = actionMessage;

    let eventScope = actionMessage.eventScope;

    // We first check if the event is in our cache, if not then we use the one provided in the message
    let eventBeforeUpdate = getCalendarEventWithId(eventId.Id);

    let idToUpdate = getItemIdToUpdate(eventId.Id, actionMessage.eventScope);

    if (eventBeforeUpdate && isRecurringMaster(eventBeforeUpdate.CalendarItemType)) {
        eventScope = EventScope.AllInstancesInSeries;
    }

    let allowLocalUpdate = isInstantUpdateSupported(eventBeforeUpdate, eventChanges);
    onUpdateItemStart(
        eventBeforeUpdate,
        eventChanges,
        eventScope,
        allowLocalUpdate,
        rangeToCreate,
        showNotification
    );
    let retryFunction = () =>
        instantCalendarEventUpdate(
            eventId,
            eventChanges,
            eventScope,
            rangeToCreate,
            actionToExecute,
            onEventUpdateSuccess,
            onEventUpdateFailed,
            errorMessage
        );

    let result: CalendarEventOperationResult = null;
    try {
        result = await actionToExecute(idToUpdate);
    } catch (error) {
        // Exception, assume update failed
        updatingCalendarEventFailed(
            eventBeforeUpdate,
            errorMessage,
            allowLocalUpdate,
            retryFunction
        );
        onEventUpdateFailed(error);
        throw error;
    }

    if (!result.isSuccessful) {
        // Either response failed or we got no item back
        updatingCalendarEventFailed(
            eventBeforeUpdate,
            errorMessage,
            allowLocalUpdate,
            retryFunction
        );
        onEventUpdateFailed(errorMessage);
        throw errorMessage;
    }

    onEventUpdateSuccess();
});

function onUpdateItemStart(
    itemBeforeUpdate: CalendarEvent,
    itemChanges: CalendarEvent,
    eventScope: EventScope,
    allowLocalUpdate: boolean,
    rangeToCreate: DateRange,
    showNotification: boolean
): void {
    if (!allowLocalUpdate) {
        return;
    }

    // Create new item with all props from original item + changes
    const folderId = itemBeforeUpdate.ParentFolderId.Id;
    let itemForSave: CalendarEvent = { ...itemBeforeUpdate, ...itemChanges };

    if (
        eventScope == EventScope.AllInstancesInSeries ||
        eventScope == EventScope.ThisAndFollowingInstances
    ) {
        if (
            itemChanges.Recurrence ||
            itemChanges.Start ||
            itemChanges.StartTimeZoneId ||
            itemChanges.End ||
            itemChanges.EndTimeZoneId ||
            eventScope == EventScope.ThisAndFollowingInstances
        ) {
            let start = itemBeforeUpdate.Start;

            // if changing the recurrence pattern, remove all existing items and recreate them
            // remove both exceptions and occurrences because changing the recurrence pattern will overwrite all exceptions
            removeCalendarEventsFromEventsCacheMatchingFilter(folderId, item => {
                return (
                    isOccurrence(item.CalendarItemType) &&
                    item.InstanceKey == itemBeforeUpdate.InstanceKey &&
                    (eventScope == EventScope.AllInstancesInSeries ||
                        (eventScope == EventScope.ThisAndFollowingInstances &&
                            !isBefore(item.Start, start)))
                );
            });

            // Expand new pattern only on series update.
            // During "from this day forward" update we don't have a way to
            // match lies with new series.
            if (eventScope == EventScope.AllInstancesInSeries) {
                let expandedItems = expandRecurringItem(itemForSave, rangeToCreate);
                prepareSeriesItemsForInstantUpdate(expandedItems.concat(itemForSave));
                let itemsWithLies = expandedItems.map(occurrence => ({
                    ...occurrence,
                    CalendarItemType: 'Occurrence' as CalendarItemTypeTypeEx,
                }));
                addCalendarEventsWithinCurrentLockedDateRanges(
                    itemForSave.ParentFolderId.Id,
                    itemsWithLies
                );
            }
        } else {
            let cachedOccurrences = filterCalendarEvents(folderId, (item: CalendarEvent) => {
                return (
                    item.InstanceKey == itemBeforeUpdate.InstanceKey &&
                    isOccurrence(item.CalendarItemType)
                );
            });

            // Update the cache entries directly
            for (let cachedOccurrence of cachedOccurrences) {
                let itemModel = cachedOccurrence;
                updateItem(itemModel, itemChanges);
                calendarEventUpdated(itemModel);
            }
        }
    }

    // If the caller wants to update the entire series from an exception, do not update the exception item
    // Series updates do not apply to exceptions
    if (
        !(
            isException(itemBeforeUpdate.CalendarItemType) &&
            eventScope == EventScope.AllInstancesInSeries
        )
    ) {
        // Handle when item changes from recurring to single, in which case we remove all old occurrences and then insert the new
        // single item
        let wasAlwaysSingleItem =
            itemBeforeUpdate.InstanceKey == null ||
            isOccurrence(itemBeforeUpdate.CalendarItemType) ||
            isException(itemBeforeUpdate.CalendarItemType);

        // Do not add to the cache if not present. TODO: except if it's an update after we saved an attachment on a new item
        addOrModifyItemInCache(itemForSave, wasAlwaysSingleItem, true /* only modify in cache */);
    }

    if (isFeatureEnabled('cal-surface-notificationBar') && showNotification) {
        const isMeetingUpdate =
            itemForSave.IsMeeting ||
            itemForSave.RequiredAttendees.length > 0 ||
            itemForSave.OptionalAttendees.length > 0;
        const notificationMessage = isMeetingUpdate
            ? strings.calendarNotificationsBarUpdateMeeting
            : strings.calendarNotificationsBarUpdateEvent;
        const notificationIcon = {
            icon: isMeetingUpdate ? ControlIcons.Send : ControlIcons.Save,
        };

        // setTimeout is to allow for any open forms to have time to close before attempting to show the notification
        setTimeout(() => {
            lazyShowCalendarNotification.import().then(showCalendarNotification => {
                showCalendarNotification(
                    'instantCalendarEventUpdate',
                    notificationMessage,
                    notificationIcon
                );
            });
        }, 0);
    }
}

function isInstantUpdateSupported(itemBeforeUpdate: CalendarEvent, itemChanges: CalendarEvent) {
    // If there is no item to update in the cache, then no instant updates
    if (!itemBeforeUpdate) {
        return false;
    }

    // If there is no loaded cache for the item being updated, this means that we are adding
    let calendarId = itemBeforeUpdate.ParentFolderId.Id;
    if (!isEventsCacheInitialized(calendarId)) {
        return false;
    }

    // We do not support instant updates for changes across calendars
    let changedCalendarId = itemChanges.ParentFolderId;
    if (changedCalendarId && changedCalendarId.Id != calendarId) {
        return false;
    }

    return true;
}
